import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  setVendorPortalToken,
  vendorPortalApi,
  type VendorPortalDashboard,
  type VendorPoSummary,
  type VendorRfqSummary,
  type VendorBillSummary,
} from '@/api/vendorPortal.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { FormGrid } from '@/components/ui/FormGrid';
import { PurchaseOrderSelect } from '@/components/selects';
import {
  createProductLineDraft,
  parseProductLines,
  ProductLineItemsEditor,
  type ProductLineDraft,
} from '@/components/forms/ProductLineItemsEditor';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { showApiError } from '@/utils/apiError';

import { notify } from '@/core/toast/notify';
export function VendorPortalDashboardPage() {
  const [dashboard, setDashboard] = useState<VendorPortalDashboard | null>(null);
  const [rfqs, setRfqs] = useState<VendorRfqSummary[]>([]);
  const [pos, setPos] = useState<VendorPoSummary[]>([]);
  const [invoices, setInvoices] = useState<VendorBillSummary[]>([]);
  const [selectedRfqId, setSelectedRfqId] = useState<number | null>(null);
  const [rfqDetail, setRfqDetail] = useState<VendorRfqSummary | null>(null);
  const [quoteDelivery, setQuoteDelivery] = useState('');
  const [quotePaymentTerms, setQuotePaymentTerms] = useState('Net 30');
  const [quoteWarranty, setQuoteWarranty] = useState('');
  const [quoteLines, setQuoteLines] = useState<ProductLineDraft[]>([
    createProductLineDraft({ quantity: '1', unitPrice: '0' }),
  ]);
  const [invoicePoId, setInvoicePoId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceLines, setInvoiceLines] = useState<ProductLineDraft[]>([
    createProductLineDraft({ quantity: '1', unitPrice: '0' }),
  ]);
  const [saving, setSaving] = useState(false);
  const { rows: paginatedRfqs, tablePaginationProps: rfqsPaginationProps } = useClientPaginatedRows(rfqs);
  const { rows: paginatedPos, tablePaginationProps: posPaginationProps } = useClientPaginatedRows(pos);
  const { rows: paginatedInvoices, tablePaginationProps: invoicesPaginationProps } = useClientPaginatedRows(invoices);

  const reload = async () => {
    const [dash, rfqList, poList, billList] = await Promise.all([
      vendorPortalApi.dashboard(),
      vendorPortalApi.listRfqs(),
      vendorPortalApi.listPurchaseOrders(),
      vendorPortalApi.listInvoices(),
    ]);
    setDashboard(dash);
    setRfqs(rfqList);
    setPos(poList);
    setInvoices(billList);
  };

  useEffect(() => {
    const token = localStorage.getItem('vendor_portal_token');
    if (!token) return;
    setVendorPortalToken(token);
    reload().catch((err) => showApiError(err, 'Failed to load vendor dashboard'));
  }, []);

  const openRfq = async (id: number) => {
    setSelectedRfqId(id);
    try {
      const detail = await vendorPortalApi.getRfq(id);
      setRfqDetail(detail);
    } catch (err) {
      showApiError(err, 'Failed to load RFQ');
    }
  };

  const submitQuotation = async () => {
    if (!selectedRfqId) {
      notify.error('Select an RFQ first');
      return;
    }
    const itemsPayload = parseProductLines(quoteLines, 'price');
    if (itemsPayload.length === 0) {
      notify.error('Add at least one quoted product line');
      return;
    }
    setSaving(true);
    try {
      await vendorPortalApi.submitQuotation({
        rfqId: selectedRfqId,
        deliveryDate: quoteDelivery || undefined,
        paymentTerms: quotePaymentTerms.trim() || undefined,
        warrantyTerms: quoteWarranty.trim() || undefined,
        items: itemsPayload,
      });
      notify.success('Quotation submitted');
      setQuoteLines([createProductLineDraft({ quantity: '1', unitPrice: '0' })]);
      await reload();
    } catch (err) {
      showApiError(err, 'Submit quotation failed');
    } finally {
      setSaving(false);
    }
  };

  const submitInvoice = async () => {
    if (!invoicePoId) {
      notify.error('Select a purchase order');
      return;
    }
    const itemsPayload = parseProductLines(invoiceLines, 'price');
    if (itemsPayload.length === 0) {
      notify.error('Add at least one invoice line');
      return;
    }
    setSaving(true);
    try {
      await vendorPortalApi.submitInvoice({
        purchaseOrderId: Number(invoicePoId),
        supplierInvoiceNumber: invoiceNumber || `INV-${Date.now()}`,
        items: itemsPayload,
      });
      notify.success('Invoice submitted');
      setInvoicePoId('');
      setInvoiceNumber('');
      setInvoiceLines([createProductLineDraft({ quantity: '1', unitPrice: '0' })]);
      await reload();
    } catch (err) {
      showApiError(err, 'Submit invoice failed');
    } finally {
      setSaving(false);
    }
  };

  if (!localStorage.getItem('vendor_portal_token')) {
    return (
      <div className="mx-auto max-w-lg pt-12">
        <PageHeader title="Vendor portal" description="Sign in to view RFQs, POs, and invoices" />
        <Link to="/vendor-portal/login" className="text-primary-600">
          Go to login
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={dashboard ? `Welcome, ${dashboard.supplierName}` : 'Vendor portal'}
        description="RFQs, purchase orders, and invoice status"
        actions={
          <Link
            to="/vendor-portal/login"
            className="text-sm text-slate-500"
            onClick={() => {
              localStorage.removeItem('vendor_portal_token');
              setVendorPortalToken(null);
            }}
          >
            Sign out
          </Link>
        }
      />

      {dashboard && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="card p-4">
            <p className="text-xs uppercase text-slate-500">Open RFQs</p>
            <p className="text-2xl font-semibold">{dashboard.openRfqs}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase text-slate-500">Open POs</p>
            <p className="text-2xl font-semibold">{dashboard.openPos}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase text-slate-500">Pending invoices</p>
            <p className="text-2xl font-semibold">{dashboard.pendingInvoices}</p>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="card p-4">
          <h2 className="mb-3 font-medium">Submit quotation</h2>
          {rfqDetail && selectedRfqId && (
            <p className="mb-2 text-sm text-slate-500">
              RFQ {rfqDetail.rfqNumber} — {rfqDetail.rfqStatus}
            </p>
          )}
          <FormGrid columns={2}>
            <DatePicker label="Delivery date" value={quoteDelivery} onChange={setQuoteDelivery} />
            <Input label="Payment terms" value={quotePaymentTerms} onChange={(e) => setQuotePaymentTerms(e.target.value)} />
            <div className="sm:col-span-2">
              <Input label="Warranty terms" value={quoteWarranty} onChange={(e) => setQuoteWarranty(e.target.value)} />
            </div>
          </FormGrid>
          <ProductLineItemsEditor lines={quoteLines} onChange={setQuoteLines} priceMode="price" />
          <Button className="mt-3" loading={saving} disabled={!selectedRfqId} onClick={() => void submitQuotation()}>
            Submit quotation
          </Button>
        </section>

        <section className="card p-4">
          <h2 className="mb-3 font-medium">Submit invoice</h2>
          <div className="grid gap-2 sm:grid-cols-2">
            <PurchaseOrderSelect label="Purchase order *" value={invoicePoId} onChange={setInvoicePoId} required />
            <Input label="Supplier invoice #" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
          </div>
          <ProductLineItemsEditor lines={invoiceLines} onChange={setInvoiceLines} priceMode="price" className="mt-3" />
          <Button className="mt-3" loading={saving} onClick={() => void submitInvoice()}>
            Submit invoice
          </Button>
        </section>
      </div>

      <section className="card overflow-x-auto p-4">
        <h2 className="mb-3 font-medium">Your RFQs</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="p-2">Number</th>
              <th className="p-2">Status</th>
              <th className="p-2">Response</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRfqs.map((r) => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="p-2">{r.rfqNumber}</td>
                <td className="p-2">{r.rfqStatus}</td>
                <td className="p-2">{r.responseStatus}</td>
                <td className="p-2">
                  <Button className="!px-2 !py-1 text-xs" variant="secondary" onClick={() => void openRfq(r.id)}>
                    Quote
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination {...rfqsPaginationProps} hasRows={rfqs.length > 0} />
      </section>

      <section className="card overflow-x-auto p-4">
        <h2 className="mb-3 font-medium">Purchase orders</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="p-2">PO</th>
              <th className="p-2">Status</th>
              <th className="p-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPos.map((po) => (
              <tr key={po.id} className="border-b border-slate-100">
                <td className="p-2">{po.poNumber}</td>
                <td className="p-2">{po.poStatus}</td>
                <td className="p-2">{po.totalAmount}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination {...posPaginationProps} hasRows={pos.length > 0} />
      </section>

      <section className="card overflow-x-auto p-4">
        <h2 className="mb-3 font-medium">Invoices & payments</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="p-2">Bill</th>
              <th className="p-2">Status</th>
              <th className="p-2">Paid</th>
            </tr>
          </thead>
          <tbody>
            {paginatedInvoices.map((b) => (
              <tr key={b.id} className="border-b border-slate-100">
                <td className="p-2">{b.billNumber}</td>
                <td className="p-2">{b.billStatus}</td>
                <td className="p-2">
                  {b.paidAmount} / {b.totalAmount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination {...invoicesPaginationProps} hasRows={invoices.length > 0} />
      </section>
    </div>
  );
}
